/**
 * VerifyConsolePage — Interim Snapshot-Based V-CIP Officer Console.
 *
 * SCOPE (be honest about it):
 *   - This is NOT production WebRTC. There is no peer-connection, no TURN
 *     server, no GPU-hosted ML. Frames are captured in the officer's browser
 *     via getUserMedia, snapshots are uploaded to /api/v1/vcip/session/.../snapshot
 *     and scored by the existing Mock providers. The UI scaffold is intentionally
 *     identical to what a real WebRTC + GPU build will eventually slot into.
 *   - The GPS gate, snapshot timeline, challenge prompts, and mismatch-audit
 *     surface ARE production-shaped — those parts will not move when WebRTC lands.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Camera, ShieldCheck, AlertOctagon, MapPin, Loader2, PlayCircle,
  StopCircle, Wifi, Hash, FileText, Compass, RefreshCcw, Accessibility
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function VerifyConsolePage() {
  const [reference, setReference] = useState("");
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stream, setStream] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [gpsConfirmed, setGpsConfirmed] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [currentChallenge, setCurrentChallenge] = useState(0);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // ------ start session ----------------------------------------------------
  async function startSession() {
    if (reference.trim().length < 2) { toast.error("Customer reference required"); return; }
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/vcip/session`, {
        customer_reference: reference.trim(),
        challenge_count: 3,
      }, { withCredentials: true });
      setSession(data);
      setCurrentChallenge(0);
      toast.success(`V-CIP session started · ${data.session_id.slice(0, 16)}…`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "GEO_NON_INDIA") {
        toast.error(`IP gate FAIL — origin not in India (${d.geo_method})`);
      } else {
        toast.error(typeof d === "string" ? d : "Failed to start V-CIP session");
      }
    } finally {
      setStarting(false);
    }
  }

  // ------ camera attach ---------------------------------------------------
  async function startCamera() {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: "user" }, audio: false,
      });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (e) {
      toast.error("Camera permission denied");
    }
  }

  function stopCamera() {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
  }

  useEffect(() => () => stopCamera(), []); // cleanup on unmount

  // ------ GPS confirm ------------------------------------------------------
  function confirmGps() {
    if (!session) { toast.error("Start a session first"); return; }
    if (!navigator.geolocation) { toast.error("Browser does not support geolocation"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await axios.post(
            `${API}/vcip/session/${session.session_id}/gps-confirm`,
            {
              lat: pos.coords.latitude,
              lon: pos.coords.longitude,
              accuracy_m: pos.coords.accuracy,
            },
            { withCredentials: true });
          setGpsConfirmed(data);
          if (data.geo_mismatch) {
            toast.warning(`GPS in India but IP geo says ${data.ip_geo_country || "non-IN"} — audit flag set (soft)`);
          } else {
            toast.success(`GPS confirmed inside India ±${Math.round(data.accuracy_m || 0)}m`);
          }
        } catch (e) {
          const d = e.response?.data?.detail;
          if (d && typeof d === "object" && d.code === "GEO_GPS_OUT_OF_INDIA") {
            toast.error("GPS gate FAIL — customer outside India (HARD block per RBI V-CIP §6.3)");
            setGpsConfirmed({ ...d, gps_in_india: false });
          } else {
            toast.error("GPS confirm failed");
          }
        } finally {
          setGpsBusy(false);
        }
      },
      (err) => {
        toast.error(`Geolocation error: ${err.message}`);
        setGpsBusy(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }

  // ------ snapshot capture ------------------------------------------------
  const captureSnapshot = useCallback(async () => {
    if (!session || !stream || !videoRef.current || !canvasRef.current) return;
    setCapturing(true);
    try {
      const v = videoRef.current;
      const c = canvasRef.current;
      c.width = v.videoWidth || 640;
      c.height = v.videoHeight || 480;
      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0, c.width, c.height);
      const b64 = c.toDataURL("image/jpeg", 0.6);

      const { data } = await axios.post(
        `${API}/vcip/session/${session.session_id}/snapshot`,
        {
          selfie_b64: b64,
          challenge_index: currentChallenge,
          note: `challenge: ${session.challenges[currentChallenge] || "n/a"}`,
        },
        { withCredentials: true });
      setSnapshots(prev => [...prev, data]);
      // advance to next challenge
      if (currentChallenge < (session.challenges?.length || 0) - 1) {
        setCurrentChallenge(currentChallenge + 1);
      }
      toast.success(`Snapshot ${data.snapshot_id.slice(0, 14)}… captured`);
    } catch (e) {
      toast.error("Snapshot failed");
    } finally {
      setCapturing(false);
    }
  }, [session, stream, currentChallenge]);

  // ------ UI ---------------------------------------------------------------
  return (
    <div style={{ maxWidth: 1320, margin: "0 auto", padding: 24 }}>
      <div className="flex items-center" style={{ gap: 12, marginBottom: 6 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Camera style={{ width: 20, height: 20, color: "#4ade80" }} />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ color: "#f1f5f9", fontSize: 26, fontWeight: 700, margin: 0 }}>
            Live V-CIP Console <span style={{ color: "#4ade80" }}>· Officer Workstation</span>
          </h1>
          <p style={{ color: "#94a3b8", margin: "2px 0 0 0", fontSize: 13 }}>
            RBI V-CIP §6.3 — Interim snapshot console. <strong style={{ color: "#fbbf24" }}>Production needs WebRTC + TURN + GPU-hosted ML.</strong>
          </p>
        </div>
      </div>

      {/* Interim banner */}
      <div data-testid="console-interim-banner" style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 10, padding: "10px 14px", marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
        <AlertOctagon style={{ width: 16, height: 16, color: "#fbbf24", flexShrink: 0 }} />
        <span style={{ color: "#fde68a", fontSize: 12 }}>
          <strong>INTERIM SNAPSHOT CONSOLE.</strong> Frames are captured in the officer&apos;s browser via <code style={{ color: "#fed7aa" }}>getUserMedia</code> and scored by Mock providers.
          The GPS gate, audit trail, and snapshot shape are production-aligned — the WebRTC + GPU ML bridge will plug in here.
        </span>
      </div>

      {/* Session creator */}
      {!session && (
        <div data-testid="console-start-card" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 20, marginBottom: 18 }}>
          <h3 style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 700, margin: "0 0 12px 0" }}>Start a new V-CIP session</h3>
          <div style={{ display: "flex", gap: 10 }}>
            <input data-testid="console-ref-input"
              value={reference} onChange={e => setReference(e.target.value)}
              placeholder="Customer reference (e.g. cust_42 or external ref)"
              style={{ flex: 1, background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: "11px 14px", color: "#f1f5f9", fontSize: 13, outline: "none" }} />
            <button data-testid="console-start-btn"
              onClick={startSession} disabled={starting}
              style={{ background: starting ? "#1e2530" : "linear-gradient(135deg, #16a34a, #15803d)", color: "white", border: "none", borderRadius: 8, padding: "11px 22px", fontSize: 13, fontWeight: 700, cursor: starting ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              {starting ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <PlayCircle style={{ width: 13, height: 13 }} />}
              Start Session
            </button>
          </div>
        </div>
      )}

      {session && (
        <>
          {/* Session header */}
          <div data-testid="console-session-bar" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: "12px 16px", marginBottom: 14, display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr", gap: 14, fontSize: 12 }}>
            <div>
              <div style={{ color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Session</div>
              <div style={{ color: "#e2e8f0", fontFamily: "ui-monospace, monospace", fontSize: 11 }}>{session.session_id}</div>
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}><Wifi style={{ width: 10, height: 10, display: "inline", marginRight: 4 }} />IP Gate</div>
              <div style={{ color: session.in_india ? "#4ade80" : "#fca5a5" }}><strong>{session.geo_country || "—"}</strong> <span style={{ color: "#64748b" }}>· {session.geo_method}</span></div>
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}><MapPin style={{ width: 10, height: 10, display: "inline", marginRight: 4 }} />GPS Gate</div>
              <div data-testid="console-gps-status" style={{ color: gpsConfirmed?.gps_in_india ? (gpsConfirmed.geo_mismatch ? "#fbbf24" : "#4ade80") : "#94a3b8" }}>
                <strong>{gpsConfirmed?.gps_in_india ? (gpsConfirmed.geo_mismatch ? "IN · mismatch" : "IN · ok") : (gpsConfirmed === null ? "pending" : "blocked")}</strong>
              </div>
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Challenge</div>
              <div style={{ color: "#e2e8f0" }}>
                <strong>{currentChallenge + 1} / {session.challenges?.length || 0}</strong> · <span style={{ color: "#94a3b8" }}>{session.challenges?.[currentChallenge] || "—"}</span>
              </div>
            </div>
          </div>

          {/* Action row */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            {!stream ? (
              <button data-testid="console-camera-start-btn"
                onClick={startCamera}
                style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "white", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <Camera style={{ width: 13, height: 13 }} />
                Attach Camera
              </button>
            ) : (
              <button data-testid="console-camera-stop-btn"
                onClick={stopCamera}
                style={{ background: "transparent", border: "1px solid #1e2530", color: "#94a3b8", borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <StopCircle style={{ width: 13, height: 13 }} />
                Detach Camera
              </button>
            )}
            <button data-testid="console-gps-btn"
              onClick={confirmGps} disabled={gpsBusy}
              style={{ background: gpsBusy ? "#1e2530" : "linear-gradient(135deg, #7c3aed, #6d28d9)", color: "white", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 700, cursor: gpsBusy ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              {gpsBusy ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Compass style={{ width: 13, height: 13 }} />}
              Confirm GPS
            </button>
            <button data-testid="console-snapshot-btn"
              onClick={captureSnapshot} disabled={!stream || capturing || !gpsConfirmed?.gps_in_india}
              style={{ background: (!stream || !gpsConfirmed?.gps_in_india) ? "#1e2530" : "linear-gradient(135deg, #16a34a, #15803d)", color: "white", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 12, fontWeight: 700, cursor: (!stream || !gpsConfirmed?.gps_in_india || capturing) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6, opacity: (!stream || !gpsConfirmed?.gps_in_india) ? 0.5 : 1 }}>
              {capturing ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Camera style={{ width: 13, height: 13 }} />}
              Capture Snapshot
            </button>
            <a href="/verify/cases" data-testid="console-cases-link"
              style={{ marginLeft: "auto", background: "transparent", border: "1px solid #1e2530", color: "#94a3b8", borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
              <Accessibility style={{ width: 13, height: 13 }} />
              Cases queue
            </a>
          </div>

          {/* Live feed + snapshots */}
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16 }}>
            <div data-testid="console-video-card" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "8px 14px", borderBottom: "1px solid #1e2530", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Customer Feed (officer browser)</span>
                <span style={{ color: stream ? "#4ade80" : "#64748b", fontSize: 10 }}>● {stream ? "LIVE" : "no feed"}</span>
              </div>
              <video ref={videoRef} autoPlay playsInline muted
                data-testid="console-video"
                style={{ width: "100%", height: 360, background: "#000", objectFit: "cover" }} />
              <canvas ref={canvasRef} style={{ display: "none" }} />
              {!stream && (
                <div style={{ padding: 16, color: "#64748b", fontSize: 11 }}>
                  Click <strong>Attach Camera</strong> above. In production this feed will be a WebRTC peer connection to the customer device with a TURN relay; the rest of the UI (GPS, snapshot, audit) stays the same.
                </div>
              )}
            </div>

            <div data-testid="console-snapshots-card" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 16, maxHeight: 480, overflowY: "auto" }}>
              <h3 style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700, margin: "0 0 10px 0", display: "flex", alignItems: "center", gap: 8 }}>
                <ShieldCheck style={{ width: 14, height: 14, color: "#4ade80" }} />
                Snapshot Timeline
                <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 11 }}>{snapshots.length} captured</span>
              </h3>
              {snapshots.length === 0 && (
                <div style={{ color: "#64748b", fontSize: 11, padding: 8 }}>
                  Snapshots appear here as you capture them. Each snapshot is appended to the session document and shows up on the case audit trail.
                </div>
              )}
              {snapshots.map((s, i) => (
                <div key={s.snapshot_id} data-testid="console-snapshot-row" style={{ background: "#080c12", borderRadius: 8, padding: 10, marginBottom: 8, fontSize: 11 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <Hash style={{ width: 10, height: 10, color: "#64748b" }} />
                    <span style={{ color: "#e2e8f0", fontFamily: "ui-monospace, monospace" }}>{s.snapshot_id}</span>
                    <span style={{ color: "#64748b", marginLeft: "auto" }}>{new Date(s.captured_at).toLocaleTimeString()}</span>
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 11 }}>
                    <FileText style={{ width: 10, height: 10, display: "inline", marginRight: 4 }} />
                    Challenge {(s.challenge_index ?? -1) + 1} · {s.note}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mismatch banner */}
          {gpsConfirmed?.geo_mismatch && (
            <div data-testid="console-mismatch-banner" style={{ marginTop: 16, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.45)", borderRadius: 10, padding: "12px 16px", display: "flex", gap: 10 }}>
              <AlertOctagon style={{ width: 16, height: 16, color: "#fbbf24" }} />
              <div>
                <div style={{ color: "#fde68a", fontSize: 12, fontWeight: 700 }}>geo_mismatch=true · audit flag set</div>
                <div style={{ color: "#fef3c7", fontSize: 11, marginTop: 2 }}>
                  GPS confirmed India but IP-side geolocation resolved to <strong>{gpsConfirmed.ip_geo_country || "non-IN"}</strong>. Session continues (per spec — IP is spoofable, don&apos;t false-block). Maker-Checker should note this in their decision.
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
